// Simple client-side router
class Router {
  constructor() {
    this.routes = {};
    this.currentRoute = null;

    // Bind events
    window.addEventListener("popstate", () => this.handleRoute());
    document.addEventListener("DOMContentLoaded", () => this.handleRoute());

    // Handle navigation clicks
    document.addEventListener("click", (e) => {
      if (e.target.matches("[data-route]")) {
        e.preventDefault();
        const route = e.target.getAttribute("data-route");
        this.navigate(route);
      }
    });
  }

normalize(path) {
  if (!path) return '/';

  // Exception: Never modify GoAccess path
  if (path.startsWith('/goaccess/')) {
    return path; // Leave as is, do not add trailing slash
  }

  if (path !== '/' && !path.endsWith('/')) {
    return path + '/';
  }

  return path;
}



  addRoute(path, handler) {
    const p = this.normalize(path);
    
    // Validate route before adding
    if (!path || typeof path !== 'string') {
      console.error('[ROUTER] Invalid route path:', path);
      return false;
    }
    
    if (!handler || typeof handler !== 'function') {
      console.error(`[ROUTER] Invalid handler for route "${p}": must be a function`);
      return false;
    }
    
    // Check for duplicate routes
    if (this.routes[p]) {
      console.warn(`[ROUTER] Overwriting existing route: "${p}"`);
    }
    
    this.routes[p] = handler;
    // [ROUTER] Route registered: "${p}"
    return true;
  }

  // Get all registered routes
  getRoutes() {
    return { ...this.routes };
  }

  // Remove a route
  removeRoute(path) {
    const p = this.normalize(path);
    if (this.routes[p]) {
      delete this.routes[p];
      // [ROUTER] Route removed: "${p}"
      return true;
    }
    return false;
  }

  // Check if a route exists
  hasRoute(path) {
    const p = this.normalize(path);
    return p in this.routes;
  }

  // Validate all routes
  validateRoutes() {
    const issues = [];
    const routes = Object.keys(this.routes);
    
    routes.forEach(route => {
      const handler = this.routes[route];
      
      if (typeof handler !== 'function') {
        issues.push({
          route,
          type: 'invalid_handler',
          message: `Handler is not a function: ${typeof handler}`
        });
      }
      
      // Check for potential conflicts
      const similar = routes.filter(r => r !== route && this.areSimilarPaths(r, route));
      if (similar.length > 0) {
        issues.push({
          route,
          type: 'similar_paths',
          message: `Similar to: ${similar.join(', ')}`
        });
      }
    });
    
    return issues;
  }

  areSimilarPaths(path1, path2) {
    // Basic similarity check for routing conflicts
    const segments1 = path1.split('/').filter(s => s);
    const segments2 = path2.split('/').filter(s => s);
    
    if (segments1.length !== segments2.length) return false;
    
    let differences = 0;
    for (let i = 0; i < segments1.length; i++) {
      if (segments1[i] !== segments2[i]) {
        differences++;
      }
    }
    
    return differences === 1; // Only one segment different
  }

  navigate(path) {
    const p = this.normalize(path);
    history.pushState(null, null, p);
    this.handleRoute();
  }

handleRoute() {
  // FIX: Router must not hijack /goaccess
  if (window.location.pathname.startsWith("/goaccess/")) {
    return;
  }

  const raw = window.location.pathname;
  const path = this.normalize(raw);

  // FIX: canonical redirect so /blog → /blog/
  if (raw !== path) {
    history.replaceState(null, null, path);
  }

  // Centraliseer route-normalisatie: altijd zoeken met trailing slash
  const normalizedPath = this.normalize(path);
  const handler = this.routes[normalizedPath];
  
  // Enhanced route handling with validation and error handling
  if (handler) {
    try {
      this.currentRoute = path;
      
      if (typeof handler !== 'function') {
        console.error(`[ROUTER] Invalid handler for route "${path}": not a function`);
        this.handle404();
        return;
      }
      
      handler();
      this.logRouteChange(path, true);

      try {
        document.dispatchEvent(
          new CustomEvent("routeChanged", { detail: { path, success: true } })
        );
      } catch (e) {
        console.warn('[ROUTER] Could not dispatch routeChanged event:', e);
      }

    } catch (error) {
      console.error(`[ROUTER] Error handling route "${path}":`, error);
      this.logRouteChange(path, false, error);
      this.handle404();
    }
  } else {
    console.warn(`[ROUTER] Route not found: "${path}"`);
    this.logRouteChange(path, false, new Error('Route not found'));
    this.handle404();
  }
}


  handle404() {
    const handler404 = this.routes['/404'];
    if (handler404 && typeof handler404 === 'function') {
      try {
        this.currentRoute = '/404';
        handler404();
        document.dispatchEvent(
          new CustomEvent("routeChanged", { detail: { path: '/404', success: false } }),
        );
      } catch (error) {
        console.error('[ROUTER] Error in 404 handler:', error);
        // Fallback 404 content
        this.showFallback404();
      }
    } else {
      this.showFallback404();
    }
  }

  showFallback404() {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.innerHTML = `
        <div class="error-page" style="text-align: center; padding: 60px 20px;">
          <h1 style="font-size: 4rem; color: #dc3545; margin-bottom: 20px;">404</h1>
          <h2 style="color: #6c757d; margin-bottom: 20px;">Page not found</h2>
          <p style="color: #6c757d; margin-bottom: 30px;">The page you are looking for does not exist or has been moved.</p>
          <button onclick="window.router.navigate('/')" 
                  style="background: #007acc; color: white; border: none; padding: 12px 24px; border-radius: 6px; cursor: pointer;">
            Back to Home
          </button>
        </div>
      `;
    }
  }

  logRouteChange(path, success, error = null) {
    // Simple logging - can be enhanced with external logging service
    const logEntry = {
      timestamp: new Date().toISOString(),
      path,
      success,
      error: error?.message || null
    };
    
    // Store in sessionStorage for debugging
    try {
      const routeLog = JSON.parse(sessionStorage.getItem('routeLog') || '[]');
      routeLog.push(logEntry);
      // Keep only last 50 entries
      if (routeLog.length > 50) {
        routeLog.splice(0, routeLog.length - 50);
      }
      sessionStorage.setItem('routeLog', JSON.stringify(routeLog));
    } catch (e) {
      // Ignore storage errors
    }
  }
}

// Import header and footer components
import { createHeader } from "./components/Header.js";
import { createFooter } from "./components/Footer.js";

// Initialize router
const router = new Router();

// Add debugging methods to router
router.enableDebug = function() {
  this.debugMode = true;
};

router.disableDebug = function() {
  this.debugMode = false;
};

router.getStats = function() {
  try {
    const routeLog = JSON.parse(sessionStorage.getItem('routeLog') || '[]');
    const totalRoutes = Object.keys(this.routes).length;
    const totalNavigations = routeLog.length;
    const successfulNavigations = routeLog.filter(log => log.success).length;
    const failedNavigations = totalNavigations - successfulNavigations;
    
    return {
      totalRoutes,
      totalNavigations,
      successfulNavigations,
      failedNavigations,
      successRate: totalNavigations > 0 ? ((successfulNavigations / totalNavigations) * 100).toFixed(2) + '%' : '0%',
      currentRoute: this.currentRoute,
      recentRoutes: routeLog.slice(-5)
    };
  } catch (error) {
    console.error('[ROUTER] Error getting stats:', error);
    return {
      error: 'Could not retrieve routing statistics'
    };
  }
};

router.testRoute = function(path) {
  const normalizedPath = this.normalize(path);
  const handler = this.routes[normalizedPath];
  
  if (!handler) {
    return { success: false, error: 'Route not found' };
  }
  
  if (typeof handler !== 'function') {
    return { success: false, error: 'Invalid handler - not a function' };
  }
  
  try {
    // Test the handler without actually navigating
    const testDiv = document.createElement('div');
    testDiv.style.display = 'none';
    document.body.appendChild(testDiv);
    
    const originalContent = document.getElementById('main-content');
    const tempContent = document.createElement('div');
    tempContent.id = 'main-content';
    testDiv.appendChild(tempContent);
    
    handler();
    
    document.body.removeChild(testDiv);
    
    return { success: true, message: 'Route handler executed successfully' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Make router globally available for debugging
if (typeof window !== 'undefined') {
  window.router = router;
}

// Re-export components with consistent naming
const createNavigation = createHeader;

export { router, createNavigation, createFooter };
